/** User-facing copy per backend code (FRONTEND_PLAN §4.9). Keyed by `details.code` first, then `error.code`. */
import type { ErrorDescription } from './describe-error'

export type Copy = Pick<ErrorDescription, 'title' | 'message' | 'retryable'>

export { BY_SUB_CODE } from './error-copy.sub'

/**
 * RecoupSlots P8/P9 codes whose backend message is written for the user (the plan-limit upgrade
 * prompt, the read-only explanation, …): we supply the title, the server supplies the message.
 */
export const SERVER_WORDED_TITLES: Readonly<Record<string, string>> = {
  PLAN_LIMIT_REACHED: 'Plan limit reached',
  BILLING_READ_ONLY: 'Account is read-only',
  BILLING_EXEMPT: 'Billing not available',
  ALREADY_SUBSCRIBED: 'Already subscribed',
  PLAN_PRICE_NOT_CONFIGURED: 'Plan not available',
}

export const BY_CODE: Readonly<Record<string, Copy & { requiresLogin?: boolean }>> = {
  VALIDATION_ERROR: {
    title: 'Check the form',
    message: 'Some fields need attention.',
    retryable: false,
  },
  UNAUTHENTICATED: {
    title: 'Signed out',
    message: 'Your session has ended. Please sign in again.',
    retryable: false,
    requiresLogin: true,
  },
  FORBIDDEN: {
    title: 'Not permitted',
    message: 'You don’t have permission to do that.',
    retryable: false,
  },
  NOT_FOUND: { title: 'Not found', message: 'We couldn’t find that.', retryable: false },
  CONFLICT: {
    title: 'Can’t save that change',
    message: 'That change conflicts with something that already exists.',
    retryable: false,
  },
  GONE: {
    title: 'No longer available',
    message: 'That link or item is no longer available.',
    retryable: false,
  },
  UNPROCESSABLE: {
    title: 'Can’t do that',
    message: 'This can’t be done right now. Please check the details and try again.',
    retryable: false,
  },
  RATE_LIMITED: {
    title: 'Please wait a moment',
    message: 'That was tried too many times in a row. Wait a moment, then try again.',
    retryable: true,
  },
  INTERNAL_ERROR: {
    title: 'Something went wrong',
    message: 'An unexpected error occurred.',
    retryable: true,
  },
  SERVICE_UNAVAILABLE: {
    title: 'Temporarily unavailable',
    message: 'The service is temporarily unavailable.',
    retryable: true,
  },
  INTEGRATION_UNAVAILABLE: {
    title: 'Calendar unreachable',
    message: 'We couldn’t confirm that with your calendar system. Please try again.',
    retryable: true,
  },
  NOT_YET_WIRED: {
    title: 'Not available',
    message: 'This feature isn’t available yet.',
    retryable: false,
  },
  INVALID_CREDENTIALS: {
    title: 'Sign-in failed',
    message: 'Email or password is incorrect.',
    retryable: false,
  },
  USER_NOT_ACTIVE: {
    title: 'Account inactive',
    message: 'This account isn’t active. Contact your administrator.',
    retryable: false,
  },
  LOGIN_RATE_LIMITED: {
    title: 'Too many attempts',
    message: 'Too many sign-in attempts. Please wait before trying again.',
    retryable: true,
  },
  TOKEN_INVALID: {
    title: 'Signed out',
    message: 'Please sign in again.',
    retryable: false,
    requiresLogin: true,
  },
  TOKEN_EXPIRED: {
    title: 'Session expired',
    message: 'Your session expired. Please sign in again.',
    retryable: false,
    requiresLogin: true,
  },
  TOKEN_REPLAY_DETECTED: {
    title: 'Signed out for security',
    message: 'For your security we signed you out everywhere. Please sign in again.',
    retryable: false,
    requiresLogin: true,
  },
  INVITE_TOKEN_INVALID: {
    title: 'Invalid invite',
    message: 'This invitation link isn’t valid.',
    retryable: false,
  },
  INVITE_EXPIRED: {
    title: 'Invite expired',
    message: 'This invitation has expired. Ask for a new one.',
    retryable: false,
  },
  USER_ALREADY_ACTIVE: {
    title: 'Already active',
    message: 'This account is already active — just sign in.',
    retryable: false,
  },
  ACTION_TOKEN_INVALID: {
    title: 'Invalid link',
    message: 'This link isn’t valid.',
    retryable: false,
  },
  ACTION_TOKEN_EXPIRED: {
    title: 'Link expired',
    message: 'This link has expired.',
    retryable: false,
  },
  ACTION_TOKEN_CONSUMED: {
    title: 'Already used',
    message: 'This link has already been used.',
    retryable: false,
  },
  ACTION_TOKEN_SCOPE_MISMATCH: {
    title: 'Wrong link',
    message: 'This link can’t be used for that action.',
    retryable: false,
  },
  OFFER_EXPIRED: {
    title: 'Appointment taken',
    message: 'That appointment is no longer available.',
    retryable: false,
  },
  IDEMPOTENCY_KEY_REQUIRED: {
    title: 'Something went wrong',
    message: 'Something interrupted that request. Please try again.',
    retryable: false,
  },
  IDEMPOTENCY_KEY_REUSED: {
    title: 'Something went wrong',
    message: 'This was already submitted with different details. Refresh the page and try again.',
    retryable: false,
  },
  IDEMPOTENCY_IN_PROGRESS: {
    title: 'Still working',
    message: 'That action is still being processed.',
    retryable: true,
  },
}
